import {
  evaluateFormulaBase,
  FormulaEditable,
  FormulaExecutable,
  fromExecutableFormula,
  parseEditableFormulaBase,
  parseExecutableFormula,
  validateFormulaSingleStatement,
} from 'src/utils/columns/customColumnUtils';
import { ContentId } from 'src/utils/constants/contentId';

import {
  CustomOnSaleEventColumn,
  OnSaleEventFormulaFields,
  OnSaleEventTableColumnId,
  OnSaleEventWithMetrics,
} from './OnSaleDiscoveryView.types';

export const ON_SALE_EVENT_FORMULA_FIELD_TO_COLUMN_ID: Record<
  OnSaleEventFormulaFields,
  OnSaleEventTableColumnId
> = {
  [OnSaleEventFormulaFields.ticketCount]: OnSaleEventTableColumnId.TicketCount,
  [OnSaleEventFormulaFields.listingCount]:
    OnSaleEventTableColumnId.ListingCount,
  [OnSaleEventFormulaFields.unitNetProceedsMin]:
    OnSaleEventTableColumnId.UnitNetProceedsMin,
  [OnSaleEventFormulaFields.unitNetProceedsP25]:
    OnSaleEventTableColumnId.UnitNetProceedsP25,
  [OnSaleEventFormulaFields.unitNetProceedsP50]:
    OnSaleEventTableColumnId.UnitNetProceedsP50,
};

export type OnSaleEventFormulaFieldsType = {
  listingCount?: number | null;
  ticketCount?: number | null;
  unitNetProceedsMin?: number | null;
  unitNetProceedsP25?: number | null;
  unitNetProceedsP50?: number | null;
};

export const ON_SALE_EVENT_FORMULA_FIELDS_TO_CID: Record<
  keyof OnSaleEventFormulaFieldsType,
  ContentId
> = {
  ['listingCount']: ContentId.ListingQty,
  ['ticketCount']: ContentId.TicketQty,
  ['unitNetProceedsMin']: ContentId.PriceMin,
  ['unitNetProceedsP25']: ContentId.PriceP25,
  ['unitNetProceedsP50']: ContentId.PriceP50,
};

const ON_SALE_EVENT_TEST_DATA = {
  metrics: {
    ltgCnt: 10,
    tktCnt: 10,
    minUNP: 100,
    p25UNP: 150,
    p50UNP: 200,
    eId: 0,
    eventVisits: null,
    eventTransactions: null,
    vstBkt: [],
    vstIdx: 0,
  },
} as unknown as OnSaleEventWithMetrics;

export const findDependentOnSaleEventTableColumnIds = (
  editableFormula: FormulaEditable
): OnSaleEventTableColumnId[] => {
  const fieldNames =
    parseEditableFormulaBase(editableFormula, true)?.map((t) => t.token) ?? [];

  return (
    Object.entries(ON_SALE_EVENT_FORMULA_FIELD_TO_COLUMN_ID)
      .filter(([key]) => fieldNames.includes(key))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(([_, value]) => value)
  );
};

export const onSaleEventToFormulaFields = (
  onSaleEvent?: OnSaleEventWithMetrics | null
): OnSaleEventFormulaFieldsType => {
  return {
    listingCount: onSaleEvent?.metrics?.ltgCnt ?? 0,
    ticketCount: onSaleEvent?.metrics?.tktCnt ?? 0,
    unitNetProceedsMin: onSaleEvent?.metrics?.minUNP ?? 0,
    unitNetProceedsP25: onSaleEvent?.metrics?.p25UNP ?? 0,
    unitNetProceedsP50: onSaleEvent?.metrics?.p50UNP ?? 0,
  };
};

export const evaluateOnSaleEventFormula = (
  formula: FormulaExecutable,
  onSaleEvent?: OnSaleEventWithMetrics | null
) => {
  const data = onSaleEventToFormulaFields(onSaleEvent);

  return evaluateFormulaBase(formula, data);
};

export const validateOnSaleEventFormula = (formula: string) => {
  return (
    parseExecutableFormula(formula) != null &&
    evaluateOnSaleEventFormula(formula, ON_SALE_EVENT_TEST_DATA) != null &&
    validateFormulaSingleStatement(formula)
  );
};

export const filterCustomColumnsForOnSaleEvent = (
  customColumns: CustomOnSaleEventColumn[]
): CustomOnSaleEventColumn[] => {
  const formulaFields = Object.keys(ON_SALE_EVENT_FORMULA_FIELDS_TO_CID);
  return customColumns.filter((column) => {
    const editableFormula = fromExecutableFormula(column.formula);
    if (!editableFormula) return false;

    const fieldNamesUsed =
      parseEditableFormulaBase(editableFormula, true)?.map((t) => t.token) ??
      [];

    return fieldNamesUsed.every((f) => formulaFields.includes(f));
  });
};
